import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

// API/Components
import axiosInstance from './axios';
import { deepCompare } from './enums';
import { isValidEmail } from './validation';
import { TIMEZONES_WINDOWS } from './time_zones';
import Toast from './toast';

import {
	Box,
	Grid,
	Typography,
	TextField,
	FormControlLabel,
	Checkbox,
	Switch,
	RadioGroup,
	Radio,
	Select,
	MenuItem,
	Button,
	Paper,
	Tooltip,
} from '@mui/material';

export default function SingleEmailSettings(props) {

	const {
		locationNum,
		emailEntries,
		selectedEmailID,
		setUnsavedChanges,
		getAllEmailSettings,
		disabled,
		userInfo,
		loading
	} = props;

	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
		clickToClose: true,
		duration: 3000,
	});

	const [userInformation, setUserInformation] = useState(userInfo);

	const [emailSettings, setEmailSettings] = useState({
		enabled: false,
		email_address: '',
		all_alarms: true,
		notifications: {
			discharge_events: false,
			auxiliary_input: false,
			system_voltage: false,
			ambient_temperature: false,
			unit_voltage_critical: false,
			unit_voltage_maintenance: false,
			unit_impedance_critical: false,
			unit_impedance_maintenance: false,
			temperature_sensor: false,
			differential_temperature: false,
			string_voltage: false,
			equipment_alarm: false,
		},
		send_analysis: {
			enabled: true,
			frequency: 0,
			attach_meas_data: false,
		},
		send_poll_status_failure: true,
		emailFormat: {
			observer_timezone: true,
			selected_timezone: 'Eastern Standard Time',
			include_location_in_subject: true,
			include_sender_in_message: false,
			include_subject_in_message: false,
			include_address_in_message: false,
			notes: {
				note1: '',
				note2: '',
			},
		},
	});

	const [emailValid, setEmailValid] = useState(false);
	
	const [currentSettingsState, setCurrentSettingsState] = useState(emailSettings);
	const [isChanged, setIsChanged] = useState(false);
	
	const requirementsMet = !emailSettings.enabled || emailValid;
	
	const navigate = useNavigate();

	useEffect(() => {
		getEmailSettings();
	}, [locationNum, selectedEmailID]);

	// Whenever the emailSettings state is updated, check if it is different from the currentSettingsState
	useEffect(() => {
		let hasChanged = !deepCompare(emailSettings, currentSettingsState);
		setIsChanged(hasChanged);
		setUnsavedChanges(hasChanged);
	}, [emailSettings, setUnsavedChanges]);

	// Form Validity Checks
	useEffect(() => { setEmailValid(isValidEmail(emailSettings.email_address)) }, [emailSettings.email_address]);

	const getEmailSettings = () => {
		// If the selected email ID is 0, do not fetch the settings
		if (selectedEmailID !== 0) {
			// Fetch the selected email settings
			axiosInstance.get(`/loc/${locationNum}/settings/email/${selectedEmailID}/`)
				.then(response => {
					// Check if the response is empty
					if (Object.keys(response.data).length === 0) {
						setToast({
							open: true,
							severity: 'error',
							message: 'No email settings found for the selected email id.',
							clickToClose: true,
							duration: 3000,
						});
						return;
					}

					// Set the current email settings and email settings state with the response data
					let settings = { ...emailSettings, ...response.data };
					setCurrentSettingsState(settings);
					setIsChanged(false);

					// Check if the user is a normal user. If they are, set the email address to the user's email address
					if (!userInfo.is_superuser && !userInfo.is_staff && settings.email_address !== userInfo.email) {
						setEmailSettings({ ...settings, email_address: userInfo.email });
					} else {
						setEmailSettings(settings);
					}
				})
				.catch(error => {
					console.error('Error fetching the specified EmailID\'s settings: ', error);
					if (error.response.status === 403) {
						navigate('/403');
					}
					setToast({
						open: true,
						severity: 'error',
						message: 'Error loading email settings information. ' + error,
						clickToClose: true,
						duration: 3000,
					});
				});
		}
	};

	const handleValueChange = (field, subField, value) => {
		setEmailSettings((prev) => ({
			...prev,
			[field]: subField ?
				{ ...prev[field], [subField]: value }
				: value,
		}));
	};

	const handleSelectAllNotifications = (value) => {
		setEmailSettings((prev) => ({
			...prev,
			all_alarms: value,
		}));
	};

	const handleSave = () => {
		if (requirementsMet) {
			axiosInstance.put(`/loc/${locationNum}/settings/email/${selectedEmailID}/`, emailSettings)
				.then(response => {
					setToast({
						open: true,
						severity: 'success',
						message: 'Email settings saved successfully.',
						clickToClose: true,
						duration: 3000,
					});
					
					console.log('Email settings saved successfully. ', response.data);

					getEmailSettings(); // Refresh the email settings after saving
					getAllEmailSettings(); // Refresh the email settings list in the parent component
				})
				.catch((error) => {
					setToast({
						open: true,
						severity: 'error',
						message: 'Email settings modification failed. ' + error,
						clickToClose: true,
						duration: 3000,
					});
					console.log(`Email settings modification failed: ${error}`);
				});
		}
	};

	const formatNotificationName = (name) => name
		.split('_')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

	// Save Section with Reset and Save buttons
	function SaveSection() {
		let tooltipText = requirementsMet ? 'Click to save changes.' : 'Please fix the highlighted issues before saving.';

		return (
			isChanged && (
				<Paper style={{ padding: 10, marginBottom: 5, marginTop: 18, border: '1px solid lightgray' }}>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="body1" marginLeft={1}>You have unsaved changes. Would you like to save?</Typography>
						<Tooltip title={tooltipText} arrow>
							<Box>
								<Button variant="contained" color="primary" disabled={!requirementsMet} onClick={handleSave}>Save Changes</Button>
							</Box>
						</Tooltip>
					</Box>
				</Paper>
			)
		);
	}

	return (
		<Box sx={{ position: 'relative' }}>
			{/* Blocking overlay */}
			{disabled && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '100%',
						backgroundColor: 'rgba(200, 200, 200, 0.5)', // Semi-transparent gray
						zIndex: 10, // Ensure it appears above the content
						cursor: 'not-allowed', // Indicate that interaction is blocked
					}}
				/>
			)}

			{/* Main Content */}
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
				clickToClose={toast.clickToClose}
				duration={toast.duration}
			/>
			<Paper sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
				<Grid container spacing={1}>
					<Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center' }}>
						<FormControlLabel
							control={
								<Switch
									checked={emailSettings.enabled}
									onChange={(e) => handleValueChange('enabled', null, e.target.checked)}
									sx={{ ml: 1 }}
								/>
							}
							label="Enabled"
						/>
					</Grid>
					<Grid item xs>
						<TextField
							fullWidth
							label="Email Address"
							value={emailSettings.email_address}
							onChange={ (e) => handleValueChange('email_address', null, e.target.value) }
							error={emailSettings.enabled && !emailValid}
							helperText={(emailSettings.enabled && !emailValid) ? 'Invalid email address' : ''}
							disabled={!emailSettings.enabled || (!userInfo.is_superuser && !userInfo.is_staff)}
							inputProps={{ maxLength: 64 }}
							sx={{
								minWidth: 200,
								flexGrow: 1,
								backgroundColor: '#ffffff'
							}}
						/>
					</Grid>
				</Grid>
				<Grid container spacing={1} sx={{ mt: 1 }}>
					<Grid item xs={12} lg={6}>
						<Paper sx={{ p: 2 }}>
							<Typography variant="subtitle1" fontWeight={600} gutterBottom>
								Email Notification Options
							</Typography>
							<FormControlLabel
								control={
									<Switch
										checked={emailSettings.all_alarms}
										onChange={(e) => handleSelectAllNotifications(e.target.checked)}
										disabled={!emailSettings.enabled}
									/>
								}
								label="All Event Notifications"
							/>
							<Box sx={{ pl: 3, mt: 2 }}>
								<Grid container spacing={1}>
									{Object.keys(emailSettings.notifications)
										.map((key) => (
											<Grid item xs={12} sm={6} key={key}>
												<FormControlLabel
													control={
														<Checkbox
															checked={emailSettings.notifications[key]}
															onChange={(e) => handleValueChange('notifications', key, e.target.checked)}
															disabled={!emailSettings.enabled || emailSettings.all_alarms }
														/>
													}
													label={formatNotificationName(key)}
												/>
											</Grid>
										))}
								</Grid>
							</Box>
							<Box sx={{ mt: 3 }}>
								<FormControlLabel
									control={
										<Switch
											checked={emailSettings.send_analysis.enabled}
											onChange={(e) => handleValueChange('send_analysis', 'enabled', e.target.checked )}
											disabled={!emailSettings.enabled}
										/>
									}
									label="Send Analysis"
								/>
								{emailSettings.send_analysis.enabled && (
									<Box sx={{ pl: 3, mt: 2 }}>
										<Typography variant="subtitle2">Frequency</Typography>
										<Select
											fullWidth
											value={emailSettings.send_analysis.frequency}
											onChange={(e) => handleValueChange('send_analysis', 'frequency', e.target.value)}
											disabled={!emailSettings.enabled}
										>
											<MenuItem value={0}>With all new measurements</MenuItem>
											<MenuItem value={1}>Once per day</MenuItem>
											<MenuItem value={2}>Once per week</MenuItem>
											<MenuItem value={3}>Only when out-of-limits</MenuItem>
										</Select>
										<FormControlLabel
											control={
												<Checkbox
													checked={emailSettings.send_analysis.attach_meas_data}
													onChange={(e) => handleValueChange('send_analysis', 'attach_meas_data', e.target.checked)}
													disabled={!emailSettings.enabled}
												/>
											}
											label="Attach Measurement Data"
										/>
									</Box>
								)}
							</Box>
							<Box sx={{ mt: 3 }}>
								<FormControlLabel
									control={
										<Switch
											checked={emailSettings.send_poll_status_failure}
											onChange={(e) => handleValueChange('send_poll_status_failure', null, e.target.checked)}
											disabled={!emailSettings.enabled}
										/>
									}
									label="Send Poll Status Failure"
								/>
							</Box>
						</Paper>
					</Grid>
					<Grid item  xs={12} lg={6}>
						<Paper sx={{ p: 2 }}>
							<Typography variant="subtitle1" fontWeight={600} gutterBottom>
								Email Format Options
							</Typography>

							{/* Timezone Options */}
							<Typography variant="subtitle2" sx={{ pl: 1, mt: 1 }}>Timezone</Typography>
							<Box sx={{ pl: 3 }}>
								<RadioGroup
									value={emailSettings.emailFormat.observer_timezone ? 'observer' : 'other'}
									onChange={(e) => handleValueChange('emailFormat', 'observer_timezone', e.target.value === 'observer')}
									disabled={!emailSettings.enabled}
								>
									<FormControlLabel
										control={<Radio />}
										label="Use Timezone from Observer Settings"
										value="observer"
										disabled={!emailSettings.enabled}
									/>
									<FormControlLabel
										control={<Radio />}
										label="Other Standard Timezone"
										value="other"
										disabled={!emailSettings.enabled}
									/>
								</RadioGroup>
								{!emailSettings.emailFormat.observer_timezone && (
									<Select
										fullWidth
										value={emailSettings.emailFormat.selected_timezone}
										onChange={(e) => handleValueChange('emailFormat', 'selected_timezone', e.target.value)}
										disabled={!emailSettings.enabled}
									>
										{TIMEZONES_WINDOWS.map((tz) => (
											<MenuItem key={tz.value} value={tz.value}>
												{tz.label}
											</MenuItem>
										))}
									</Select>
								)}
							</Box>

							{/* Email Message Format Checkboxes */}
							<Typography variant="subtitle2" sx={{ pl: 1, mt: 1 }}>Email Message</Typography>
							<Box sx={{ display: 'flex', flexDirection: 'column', pl: 3 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={emailSettings.emailFormat.include_location_in_subject}
											onChange={(e) => handleValueChange('emailFormat', 'include_location_in_subject', e.target.checked)}
											disabled={!emailSettings.enabled}
										/>
									}
									label="Include Location in email subject"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={emailSettings.emailFormat.include_sender_in_message}
											onChange={(e) => handleValueChange('emailFormat', 'include_sender_in_message', e.target.checked)}
											disabled={!emailSettings.enabled}
										/>
									}
									label="Include sender in email message"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={emailSettings.emailFormat.include_subject_in_message}
											onChange={(e) => handleValueChange('emailFormat', 'include_subject_in_message', e.target.checked)}
											disabled={!emailSettings.enabled}
										/>
									}
									label="Include subject in email message"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={emailSettings.emailFormat.include_address_in_message}
											onChange={(e) => handleValueChange('emailFormat', 'include_address_in_message', e.target.checked)}
											disabled={!emailSettings.enabled}
										/>
									}
									label="Include address and description in email message"
								/>
								<TextField
									fullWidth
									label="Note 1"
									value={emailSettings.emailFormat.notes.note1}
									onChange={(e) => handleValueChange(
										'emailFormat',
										'notes', {
											...emailSettings.emailFormat.notes,
											note1: e.target.value,
										}
									)}
									disabled={!emailSettings.enabled}
									inputProps={{ maxLength: 50 }}
									sx={{ mt: 1 }}
								/>
								<TextField
									fullWidth
									label="Note 2"
									value={emailSettings.emailFormat.notes.note2}
									onChange={(e) =>
										handleValueChange('emailFormat', 'notes', {
											...emailSettings.emailFormat.notes,
											note2: e.target.value,
										})
									}
									disabled={!emailSettings.enabled}
									inputProps={{ maxLength: 50 }}
									sx={{ mt: 1 }}
								/>
							</Box>
						</Paper>
					</Grid>
				</Grid>
			</Paper>
			<SaveSection />
		</Box>
	);
}